(ns motorsaif.shared.form
  (:require
   [malli.util :as mu]
   [motorsaif.data-utils :as data-utils]
   [motorsaif.diff :as diff]
   [clojure.string :as str]
   [motorsaif.i18n :as i18n :refer [tr]]
   [motorsaif.log :as log]
   [motorsaif.malli :as malli]
   [motorsaif.utils :as utils]
   [reagent.core :as r]))

(defn event->value [e]
  (let [?target (and e
                     (aget e "target"))
        val (if ?target
              (.-value ?target)
              e)
        val (if (string? val)
              (if (empty? val)
                nil
                (str/trim val))
              val)]
    val))

(defn regex-filter-items [query items & [get-fn]]
  (let [text query]
    (filter
     (fn [v]
       (re-find
        (re-pattern (str "(?i)^" (utils/regex-escape-str (str query))))
        (str (if get-fn
               (get-fn v)
               v))))
     items)))

(defn remove-image [{:keys [set-val after-remove path]} itm]
  (let [{db-id :db/id
         uuid :uuid} itm]

    ;; Remove the upload
    (when uuid
      (set-val [:uploads :raw] (fn [coll]
                                 (vec (filter #(not= (:uuid %) uuid) coll)))))

    (set-val
     (pop path)
     (fn [m]
       (let [k (last path)
             coll (get m k)
             coll (vec (remove nil? (filter #(not= (:db/id %) db-id) coll)))]
         (if (empty? coll)
           (dissoc m k)
           (assoc m k coll)))))

    (when after-remove
      (after-remove itm))))

(defn combined-images [existing uploaded]
  (vals (reduce (fn [acc itm]
                  (let [{?id :db/id
                         ?uuid :uuid} itm
                        ;; ?up-id (get-in itm [:response :media :db/id])
                        k (or ?id ?uuid)

                        {:media/keys [order] :as ?existing} (get acc ?id)
                        ;; The real order comes from the existing images
                        ;; since that's what's updated on drag
                        itm (-> itm
                                (cond-> order
                                  (assoc :media/order order)))]
                    (assoc acc k itm)))
                {}
                (concat
                 existing
                 (map-indexed (fn [idx v]
                                (let [{:keys [*response uuid preview]} v
                                      {?db-id :db/id} @*response]
                                  ;; We assoc the uploader object to meta map here
                                  ;; ;; Let's delegate this
                                  (with-meta
                                    {:uuid uuid
                                     :db/id ?db-id
                                     :media/order (+ idx (count existing)) ;; cannot mutate this........
                                     :media/preview preview}
                                    v)))
                              uploaded)))))

(defn make-form
  "Returns a map that can be used to render full input or parameter helpers to be mixed into form elements
  to handle change events, set values, etc.

           [input {:style 'bootstrap'
                   :label 'Model'
                   :path [:car :car/make]}]
  State is held internally and synchronized with change-watch

  Default and init data.
  Init data is set in beginning and sent along with request.
  "
  [{:keys [change-watch init-fn init-data schema on-submit
           render-components]}]
  (let [
        {:keys [input
                media
                select
                check
                radio
                uploader
                presentable-error]} render-components
        ;; TODO this init shit can be done better than this
        init-data-fn (fn [] (or (when init-fn (init-fn)) init-data))
        *init-data (atom (init-data-fn))

        ;; Realtime validation runs on every set-val and once on form init
        *data (r/atom @*init-data)
        *validated-data (atom nil)
        *new-data (atom {})
        *state (atom :new) ; #{:new, :valid, invalid, submitted, submitting}
        *realtime-validation (r/atom nil)
        realtime-validate! (fn []
                             (reset! *realtime-validation (malli/validate schema @*data)))

        reset-form! (fn []
                      (log/info "Reset form")
                      (reset! *data (init-data-fn))
                      (reset! *validated-data @*init-data)
                      (reset! *new-data {})
                      (reset! *state :new))

        set-val (fn [path data & args]
                  ;; Try validating here too
                  (let [path (data-utils/ensure-vec path)]
                    (if (fn? data)
                      (apply (partial swap! *data update-in path data) args)
                      (swap! *data assoc-in path data))
                    (swap! *new-data assoc-in path (get-in @*data path))
                    (realtime-validate!)))

        retract-entity-fn
        (fn [id]
          (set-val [:retract-entities]
                   (fnil #(vec (distinct (conj % [:db.fn/retractEntity id]))) [])))

        retract-fn
        (fn [{:keys [parent-id key item-id]}]
          (set-val [:retracts]
                   (fnil #(vec (distinct (conj % [:db/retract parent-id key item-id]))) [])))
        get-diff
        (fn []
          (first (diff/diff
                  (:decoded @*validated-data)
                  @*init-data)))

        trim-path (fn [path]
                    (-> path
                        (cond-> (fn? (last path))
                          pop)))

        on-change (fn [path e & {:keys [keep-empty?]}]
                    (let [last-path (last path)
                          value (-> (event->value e)
                                    (cond-> (fn? last-path)
                                      last-path))
                          path (trim-path path)]

                      (if (and (not keep-empty?)
                               (empty? (str value)))
                        (set-val (pop path) #(dissoc % (last path)))
                        (set-val path value))))

        get-val (fn [path]
                  (get-in @*data (data-utils/ensure-vec path)))


        error (fn [path]
                (get-in (:human @*validated-data) (conj path 0)))

        state (fn [path]
                (let [init-value (get-in @*init-data path)
                      current-value (get-in @*data path)
                      has-schema? (mu/get-in schema path)
                      realtime-error (get-in
                                      @*realtime-validation
                                      (concat [:human] path))]

                  (cond
                    (= current-value init-value) :untouched

                    ;; Implicit valid, no schema
                    (nil? has-schema?) :valid

                    realtime-error :error
                    (not realtime-error) :valid
                    :else
                    :unhandled)))

        validate-fn (fn [new-state]
                      (when-not (= :new @*state)
                        (let [validated (malli/validate schema new-state)]
                          (cond (:valid validated)
                                (reset! *state :valid)
                                :else (reset! *state :invalid))
                          (reset! *validated-data validated))))

        submit-form (fn --submit [e]
                      (let [{:keys [valid invalid reset]} on-submit]

                        (when (and e (.-preventDefault e)) ; MOBILE/WEB COMPAT CHECK
                          (.preventDefault e))

                        (validate-fn @*data)

                        (condp = @*state
                          :new (do
                                 (reset! *state :submit)
                                 (--submit e))
                          :valid (do
                                   (valid {:all-data (:decoded @*validated-data)
                                           :new-data (get-diff)})
                                   (when reset
                                     (reset-form!)))
                          :invalid (when invalid (invalid @*validated-data))
                          (println "No input") ; no input
                          )))

        form-params {:class "default"
                     :on-submit submit-form}]

    ;; Validate once on init
    (realtime-validate!)

    ;; We validate every change for now
    (when schema
      (add-watch *data :validate-watch
                 (fn [_ _ _ new-state]
                   (validate-fn new-state))))

    (when change-watch
      (add-watch *data :change-watch
                 (fn [_ _ _ new-state]
                   (change-watch new-state))))

    {;; Components
     :input
     (fn -input
       ([path]
        (-input {} path))
       ([opts path]
        (let [trimmed-path (trim-path path)
              ?default-value (get-in @*init-data trimmed-path)]
          [input
           {:path trimmed-path
            :error error
            :default-value ?default-value
            :state state
            :get-val get-val
            :opts opts
            :on-change (partial on-change path)}])))

     :uploaded-media
     (fn -media
       ([path] (-media {} path))
       ([opts path]
        [media {:path path
                :get-val get-val
                :retract-fn retract-fn
                :opts opts
                :state state
                :error error
                :set-val set-val}]))

     :select
     (fn -select
       ([choices path]
        (-select choices {} path))
       ([choices opts path]
        (let [trimmed-path (trim-path path)
              ?default-value (get-in @*init-data trimmed-path)]
          [select
           {:path trimmed-path
            :error error
            :choices choices
            :state state
            :set-val set-val
            :default-value ?default-value
            :get-val get-val
            :opts opts
            :on-change (partial on-change path)}])))

     :check
     (fn -check
       ([choices path] (-check choices {} path))
       ([choices opts path]
        (let [trimmed-path (trim-path path)
              ?default-value (get-in @*init-data trimmed-path)]
          [check {:opts opts
                  :path path
                  :choices choices
                  :state state
                  :error error
                  :default-value ?default-value
                  :get-val get-val
                  :set-val set-val}])))

     :radio
     (fn -radio
       ([choices path] (-radio choices {} path))
       ([choices opts path]
        (let [trimmed-path (trim-path path)
              ?default-value (get-in @*init-data trimmed-path)]
          [radio {:opts opts
                  :path path
                  :choices choices
                  :state state
                  :error error
                  :default-value ?default-value
                  :get-val get-val
                  :set-val set-val}])))

     :uploader
     (fn -uploader [path & [opts]]
       [uploader
        (merge opts {:path path
                     :get-val get-val
                     :state state
                     :error error
                     :set-val set-val})])
     ;; Utils
     :error error
     :presentable-error (fn [path]
                          (presentable-error (error path)))
     :get-val get-val
     :retract-fn retract-fn
     :retract-entity-fn retract-entity-fn
     :get-diff get-diff
     :set-val set-val
     :validate-fn validate-fn
     :*new-data *new-data
     :*validated-data *validated-data
     :*init-data *init-data
     :submit-form submit-form
     :*data *data
     :*realtime-validation *realtime-validation
     :reset-form! reset-form!
     :form-params form-params
     :on-change on-change}))
